import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { isValidEmail } from '../util/isValidEmail';

import './EmailInput.css';

// key as required prop to ensure expected behavior for controlled component with 'draft'
// https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key

interface Props {
  key: string;
  name: string;
  className?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValidEmail: (validEmail: string) => void;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  onFocus?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  enableMultiple?: boolean;
}

export default class EmailInput extends React.Component<Props, {}> {
  static defaultProps = {
    name: 'email',
    onValidEmail: () => {},
    enableMultiple: true
  };

  state = {
    rawValue: this.props.value,
    isError: false
  };

  handleFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };
  handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.which === 32) {
      e.preventDefault();
    }
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    let isError = false;

    // Check for multiple emails (comma or semicolon separated)
    const emails = inputValue.split(/[;,]+/).filter(Boolean);
    if (!this.props.enableMultiple && emails.length > 1) {
      isError = true;
    }

    // validate emails
    if (emails.find((email) => !isValidEmail(email))) {
      isError = true;
    }

    if (this.props.onChange) {
      this.props.onChange(e);
    }
    this.setState({ rawValue: inputValue, isError });
    // remove commas and semicolons if not allowing multiple emails
    const sanitizedInput = this.props.enableMultiple
      ? inputValue
      : inputValue.trim().replace(/[;,]+/g, '');
    this.props.onValidEmail(isError ? '' : sanitizedInput);
  };

  render() {
    const value = this.state.rawValue || this.props.value;
    const className = this.props.className || this.props.name;
    const autoComplete = this.props.autoComplete || 'email';
    const autoFocus = this.props.autoFocus || false;
    const isPresent = value && value.length > 0;
    const invalidColor = isPresent ? 'red' : 'grey';
    const icon = !this.state.isError ? (
      <FontAwesomeIcon icon="check-circle" color="grey" />
    ) : isPresent ? (
      <FontAwesomeIcon icon="exclamation-circle" color={invalidColor} />
    ) : (
      ''
    );
    return (
      <div className="text-with-icon w-100">
        {icon}
        <input
          type="email"
          disabled={this.props.disabled}
          id={this.props.name}
          name={this.props.name}
          className={className}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          placeholder={this.props.placeholder}
          value={value}
          onFocus={(e) => this.handleFocus(e)}
          onBlur={(e) => this.handleBlur(e)}
          onChange={(e) => this.handleChange(e)}
          onKeyDown={(e) => this.handleKeyDown(e)}
        />
      </div>
    );
  }
}
